
import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutDashboard, Upload, Users, Mail, BarChart, Settings } from 'lucide-react';
import { useUser } from '@/context/UserContext';
import AdminLogin from '@/components/admin/AdminLogin';
import ContentManagementTab from '@/components/admin/ContentManagementTab';
import MassUploadTab from '@/components/admin/MassUploadTab';
import UsersTab from '@/components/admin/UsersTab';
import InvitesTab from '@/components/admin/InvitesTab';
import AnalyticsTab from '@/components/admin/AnalyticsTab';
import SettingsTab from '@/components/admin/SettingsTab';
import AdminStatusToggle from '@/components/AdminStatusToggle';
import { toast } from "sonner";
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { user } = useUser();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Check if already authenticated via localStorage or if user is admin
    const adminAuth = localStorage.getItem('hormony_admin_auth');
    
    if (adminAuth === 'true' || (user && user.isAdmin)) {
      setIsAuthenticated(true);
    } else if (user && typeof user.isAdmin !== 'undefined' && !user.isAdmin) {
      // Only show this toast if we know for sure they're not an admin
      toast.error("You don't have admin privileges. Contact an administrator for access.");
    }
  }, [user]);
  
  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('hormony_admin_auth', 'true');
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('hormony_admin_auth');
  };
  
  // Don't show the admin login form to users who are logged in but not admins
  if (!isAuthenticated && user && typeof user.isAdmin !== 'undefined' && !user.isAdmin) {
    return (
      <Layout>
        <div className="container py-8">
          <h1 className="text-2xl font-bold mb-4">Admin Access Required</h1>
          <p>You need administrator privileges to access this page. Please contact an administrator if you need access.</p>
          <div className="mt-6 mb-2">
            <AdminStatusToggle />
          </div>
          <Button onClick={() => window.location.href = '/dashboard'} className="mt-4">
            Return to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <Layout>
        <AdminLogin onLogin={handleLogin} />
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container py-8">
        {/* Admin Status Toggle moved to the top of the page */}
        <div className="mb-6">
          <AdminStatusToggle />
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <Button variant="outline" onClick={handleLogout}>
            Logout
          </Button>
        </div>
        
        <Tabs defaultValue="content" className="mt-6">
          <TabsList className="mb-6 w-full flex flex-wrap justify-center md:justify-start overflow-x-auto">
            <TooltipProvider delayDuration={300}>
              {/* Content Management Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="content" className="flex items-center gap-2">
                    <LayoutDashboard className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">Content Management</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>Content Management</p>
                  </TooltipContent>
                )}
              </Tooltip>
              
              {/* Mass Upload Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="massUpload" className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">Mass Upload</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>Mass Upload</p>
                  </TooltipContent>
                )}
              </Tooltip>
              
              {/* User Management Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="users" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">User Management</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>User Management</p>
                  </TooltipContent>
                )}
              </Tooltip>
              
              {/* Invites Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="invites" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">Invites</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>Invites</p>
                  </TooltipContent>
                )}
              </Tooltip>
              
              {/* Analytics Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="analytics" className="flex items-center gap-2">
                    <BarChart className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">Analytics</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>Analytics</p>
                  </TooltipContent>
                )}
              </Tooltip>
              
              {/* Settings Tab */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger value="settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {!isMobile && <span className="whitespace-nowrap">Settings</span>}
                  </TabsTrigger>
                </TooltipTrigger>
                {isMobile && (
                  <TooltipContent side="bottom">
                    <p>Settings</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </TabsList>
          
          <TabsContent value="content" className="space-y-6">
            <ContentManagementTab />
          </TabsContent>
          
          <TabsContent value="massUpload">
            <MassUploadTab />
          </TabsContent>
          
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          
          <TabsContent value="invites">
            <InvitesTab />
          </TabsContent>
          
          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>
          
          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Admin;
